/**
 * Global date/time utility module.
 *
 * Rules:
 * - This file is the single source of truth for shared date/time formatting and difference calculations.
 * - All new common date conversions must be added here instead of duplicating logic elsewhere.
 * - Implementations in this file must use `date-fns` helpers instead of manual `Date` math.
 *
 * If you need a new reusable date helper, add it here using `date-fns` and then consume it from callers.
 */

/** Input type accepted by all date helpers — ISO string, epoch millis, or Date object. */
export type DateInput = Date | string | number;

import {
  addDays,
  addMinutes,
  addMonths,
  addYears,
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  intervalToDuration,
  isAfter,
  isBefore,
  isValid,
  parse,
  startOfDay,
  startOfMinute,
  startOfMonth,
  subYears,
  roundToNearestMinutes,
} from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

import { logger } from '@/lib/logger';

/** A bare calendar date carrying no time and no offset, e.g. "2026-08-12". */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses a bare `yyyy-MM-dd` calendar date as **local** midnight.
 *
 * ## Invariants:
 * - Never pass a calendar date to `new Date(string)`: ES-262 reads the date-only form as UTC
 *   midnight, so a behind-UTC zone renders the previous day (PB-4476).
 *   Why local: legacy used moment, which reads date-only strings as local — this keeps both sites
 *   agreeing.
 */
export const parseCalendarDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Converts a DateInput to a Date object.
 * Handles ISO strings, bare calendar dates, epoch millis (number), numeric strings
 * (e.g. "1711547400000"), and existing Date objects.
 */
const toDate = (date: DateInput): Date => {
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);
  const num = Number(date);
  if (Number.isFinite(num)) return new Date(num);
  if (DATE_ONLY_PATTERN.test(date)) return parseCalendarDate(date);
  return new Date(date);
};

// A past start is dropped: copied or bookmarked reserve URLs freeze the day they were browsed.
export const parseUrlPermitStart = (param: string | null, now: Date): Date | null => {
  if (!param) return null;
  const parsed = toDate(param);
  if (!isValid(parsed)) return null;
  return isBefore(parsed, startOfDay(now)) ? null : parsed;
};

/**
 * Rounds a date UP to the next 15-minute boundary.
 * E.g. 10:03 → 10:15, 10:15 → 10:15 (exact boundaries are kept), 10:16 → 10:30.
 */
export const roundToNext15Minutes = (date: DateInput): Date => {
  return roundToNearestMinutes(toDate(date), { nearestTo: 15, roundingMethod: 'ceil' });
};

/**
 * Rounds a date UP to the next whole minute (ceil). An exact minute boundary is kept.
 * E.g. 10:44:59 → 10:45, 10:45:00 → 10:45:00, 10:44:00.500 → 10:45.
 *
 * Presents a clock-block covered window by its exclusive upper bound: a rate that
 * covers through 10:44:59 (enter + 2h − 1s) is "valid until 10:45", so the customer-facing
 * exit is 10:45 and the duration a full 2h — never the last-covered second's minute (10:44 / 1:59).
 */
export const ceilToMinute = (date: DateInput): Date => {
  return roundToNearestMinutes(toDate(date), { nearestTo: 1, roundingMethod: 'ceil' });
};

/**
 * Moves a URL-supplied hourly window that starts in the past to start "now" (the next 15-minute
 * slot, same as the default start). The exit is kept when it still falls after the new start;
 * otherwise it moves to the new start + the window's original length (or `fallbackDurationMins`
 * when the original window was empty or inverted). A window starting at or after `earliest`
 * (default `now`) is returned unchanged.
 *
 * ## Invariants:
 * - A start inside the tenant's allowReservationInPast grace window (`earliest` = now − grace) is
 *   kept. Why: the home page and search bar let the customer pick it, so the search page must not
 *   move it (old-site parity, PB-8784). Anything earlier opens on now (PB-9151).
 */
export const clampPastUrlWindow = (
  enter: Date,
  exit: Date,
  now: Date,
  fallbackDurationMins: number,
  earliest: Date = now
): { enter: Date; exit: Date } => {
  if (enter.getTime() >= Math.min(earliest.getTime(), now.getTime())) return { enter, exit };
  const nextEnter = roundToNext15Minutes(now);
  if (exit.getTime() > nextEnter.getTime()) return { enter: nextEnter, exit };
  const originalMs = exit.getTime() - enter.getTime();
  const durationMs = originalMs > 0 ? originalMs : fallbackDurationMins * 60_000;
  return { enter: nextEnter, exit: new Date(nextEnter.getTime() + durationMs) };
};

/**
 * Formats the duration between two dates as a compact string (e.g. "1h", "2h 30m").
 * Returns "—" if the duration is zero or negative.
 */
export const formatDurationCompact = (start: DateInput, end: DateInput): string => {
  const totalMinutes = differenceInMinutes(toDate(end), toDate(start));
  if (totalMinutes <= 0) return '—';
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = differenceInHours(toDate(end), toDate(start));
  const remainingMinutes = totalMinutes % 60;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

/**
 * The stay between two dates for the duration chip, in months, days, hours and minutes
 * ("4 days", "1 month 2 days", "2h 30m"). Returns "—" if the stay is zero or negative.
 *
 * ## Invariants:
 * - An end on the minute before an hour boundary (…:59) is an inclusive end, so it counts as the
 *   full hour: 2:00 PM Tue → 1:59 PM Sat reads "4 days", not "95h 59m". Rate-cycle ends are inclusive.
 * - Months and days are calendar-based (from the start), so a month is the start's own month length.
 * - `formatDurationCompact` keeps its hours-and-minutes format for its other callers.
 * - `inclusiveEnd: false` turns off the :59 rule for an end that is already an exclusive minute
 *   (PB-8094's same-price exits, rounded up by `ceilToMinute`). There a whole :59 end is the real
 *   end, so 1:00 PM → 2:59 PM reads "1h 59m" and matches the exit shown beside it.
 */
export const formatStayDuration = (
  start: DateInput,
  end: DateInput,
  { inclusiveEnd = true }: { inclusiveEnd?: boolean } = {}
): string => {
  const from = startOfMinute(toDate(start));
  let to = startOfMinute(toDate(end));
  const totalMinutes = differenceInMinutes(to, from);
  if (totalMinutes <= 0) return '—';
  if (inclusiveEnd && (totalMinutes + 1) % 60 === 0) to = addMinutes(to, 1);
  const { years = 0, months = 0, days = 0, hours = 0, minutes = 0 } = intervalToDuration({
    start: from,
    end: to,
  });
  const totalMonths = years * 12 + months;
  const parts: string[] = [];
  if (totalMonths > 0) parts.push(`${totalMonths} ${totalMonths === 1 ? 'month' : 'months'}`);
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.join(' ');
};

/**
 * Formats a 24-hour time string ("HH:mm") to 12-hour format ("h:mm AM/PM").
 */
export const formatTime12h = (time24: string): string => {
  const [hourStr, minuteStr] = time24.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr ?? '00';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${ampm}`;
};

/** Formats a date as yyyy-MM-dd (local calendar date) for API date-only fields. */
export const formatApiDate = (date: DateInput) => format(toDate(date), 'yyyy-MM-dd');

/**
 * Formats a DatePicker month value as the corporate-validation reports `month`
 * query param: the start-of-month local instant serialized as a UTC ISO string.
 *
 * Mirrors the legacy reservation app's `moment(value).startOf('month').toISOString()`
 * (Reports.tsx), so the api's Joda `DateTime month` param resolves to the same
 * calendar month. The new app previously sent a bare `yyyy-MM` string
 * (`format(date,'yyyy-MM')`), which the backend parsed with a different month
 * boundary in some timezones. (PB-3126)
 */
export const toReportMonthParam = (date: DateInput): string =>
  startOfMonth(toDate(date)).toISOString();

/** Formats a date as MM/DD/YYYY for UI display. */
export const formatDate = (date: DateInput) => {
  return format(toDate(date), 'MM/dd/yyyy');
};

/**
 * Returns the number of whole calendar days between two dates (date2 - date1).
 * Time-of-day is ignored; only the calendar dates are considered.
 */
export const getDaysBetweenDates = (date1: DateInput, date2: DateInput) => {
  return differenceInCalendarDays(toDate(date2), toDate(date1));
};

/**
 * Whole elapsed 24-hour days from `from` to `to`, truncated toward zero (PB-7554).
 *
 * Unlike {@link getDaysBetweenDates} this counts elapsed time, not calendar dates - the same
 * measure the API's Joda `Days.daysBetween` uses for the past-due hold, so the banner and the gate
 * agree on the day a balance crosses the limit.
 */
export const getWholeDaysElapsed = (from: DateInput, to: DateInput): number =>
  differenceInDays(toDate(to), toDate(from));

/**
 * Returns the absolute time difference between two dates as an `HH:mm:ss` string.
 * Assumes the caller is interested only in the duration, not which date is earlier.
 */
export const getDifferenceTime = (date1: DateInput, date2: DateInput) => {
  const totalSeconds = Math.abs(differenceInSeconds(toDate(date2), toDate(date1)));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

/** Formats a date as "h:mm a" (e.g., "2:30 PM") for reservation time display. */
export const formatReservationTime = (date: DateInput) => {
  return format(toDate(date), 'h:mm a');
};

/** Formats a date as "EEE, dd MMM yyyy" (e.g., "Mon, 05 Jan 2026") for reservation date display. */
export const formatReservationDate = (date: DateInput) => {
  return format(toDate(date), 'EEE, dd MMM yyyy');
};

/** Formats a date as "EEE dd MMM" (e.g., "Wed 10 Dec") for compact reservation date display. */
export const formatReservationDateShort = (date: DateInput) => {
  return format(toDate(date), 'EEE dd MMM');
};

/**
 * Formats an instant in the garage's timezone.
 *
 * Falls back to browser-local, NOT UTC like {@link formatDateInGarageTz} — an untimezoned
 * garage must keep rendering as it did, which is also how legacy's `formatGarageZone`
 * degrades. (PB-3393 → PB-5067)
 */
export const formatInGarageTz = (date: DateInput, fmt: string, timezone?: string): string =>
  timezone ? formatInTimeZone(toDate(date), timezone, fmt) : format(toDate(date), fmt);

/** Garage-timezone counterpart of {@link formatReservationTime} — use for any api instant. */
export const formatReservationTimeInGarageTz = (date: DateInput, timezone?: string) =>
  formatInGarageTz(date, 'h:mm a', timezone);

/** Garage-timezone counterpart of {@link formatReservationDate} — "EEE, dd MMM yyyy". */
export const formatReservationDateInGarageTz = (date: DateInput, timezone?: string) =>
  formatInGarageTz(date, 'EEE, dd MMM yyyy', timezone);

/**
 * Formats a duration in minutes as "H:MM <unit>" (e.g., 60 → "1:00 HR", 90 → "1:30 HR").
 * Used for the reservation duration pill. Negative or zero values clamp to "0:00 <unit>".
 * The unit suffix is caller-provided so callers can pass a translated string.
 */
export const formatHourMinutePill = (totalMinutes: number, unitLabel = 'HR'): string => {
  const safeMinutes = Math.max(0, Math.floor(totalMinutes));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  return `${hours}:${minutes.toString().padStart(2, '0')} ${unitLabel}`;
};

/** Returns the rounded duration in hours between two dates. */
export const getDurationHours = (start: DateInput, end: DateInput) => {
  return differenceInHours(toDate(end), toDate(start));
};

/** Minimum span (in days) of a monthly parking booking. */
export const MONTHLY_BOOKING_DAYS = 30;

/** Returns the exit date for a monthly booking starting at `enterDate`. */
export const computeMonthlyExitDate = (enterDate: DateInput): Date => {
  return addDays(toDate(enterDate), MONTHLY_BOOKING_DAYS);
};

/**
 * Returns the duration in whole minutes between two dates.
 *
 * Seconds and milliseconds are floored on BOTH endpoints before the diff so the
 * result matches the displayed `h:mm` times — e.g. 6:05:43 → 8:05:00 is 2:00,
 * not 1:59. Mirrors the legacy reservation app's `getDurationFull`
 * (vm-reservation-website constants.ts: `.second(0).millisecond(0)` on both
 * ends). This is the single source of truth for fixed enter→exit duration
 * displays — do NOT use raw `differenceInMinutes` for a duration pill. (Live
 * "remaining from now" countdowns are a different calculation and intentionally
 * keep sub-minute precision against the current time.)
 */
export const getDurationMinutes = (start: DateInput, end: DateInput) => {
  return differenceInMinutes(startOfMinute(toDate(end)), startOfMinute(toDate(start)));
};

/**
 * Formats a duration in minutes as a zero-padded "HH:MM" string (e.g. 179 → "02:59",
 * 60 → "01:00", 45 → "00:45"). Both hours and minutes are padded to two digits.
 * Negative or zero values clamp to "00:00". Used for the reservation duration pill.
 */
export const formatDurationHoursMinutes = (totalMinutes: number): string => {
  const safeMinutes = Math.max(0, Math.floor(totalMinutes));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/** Returns true if the given date is before today (start of day). */
export const isDateBeforeToday = (date: DateInput) => {
  return isBefore(toDate(date), startOfDay(new Date()));
};

/** Returns ordinal suffix for a day number (e.g., 1 → "1st", 10 → "10th"). */
function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/** Formats a day number with ordinal suffix (e.g., 1 → "1st", 22 → "22nd"). */
export function formatOrdinalDay(day: number): string {
  return `${day}${getOrdinalSuffix(day)}`;
}

/** Formats a date as "EEE d{th} MMM yyyy h:mm a" (e.g., "Wed 10th Dec 2025 2:30 PM"). */
export const formatDateTimeWithDay = (date: DateInput) => {
  const value = toDate(date);
  const day = value.getDate();
  const ordinal = `${day}${getOrdinalSuffix(day)}`;
  const rest = format(value, 'MMM yyyy h:mm a');
  const dayName = format(value, 'EEE');
  return `${dayName} ${ordinal} ${rest}`;
};

/** Garage-timezone counterpart of {@link formatDateTimeWithDay}. */
export const formatDateTimeWithDayInGarageTz = (date: DateInput, timezone?: string) => {
  const day = Number(formatInGarageTz(date, 'd', timezone));
  const ordinal = `${day}${getOrdinalSuffix(day)}`;
  return `${formatInGarageTz(date, 'EEE', timezone)} ${ordinal} ${formatInGarageTz(date, 'MMM yyyy h:mm a', timezone)}`;
};

/** Formats a date as "EEE dd MMM yyyy" (e.g., "Wed 10 Dec 2025") — no time component. */
export const formatDateWithDay = (date: DateInput) => {
  return format(toDate(date), 'EEE dd MMM yyyy');
};

/** Formats a date as "MMMM dd, yyyy" (e.g., "October 01, 2026") — US long form. */
export const formatDateLong = (date: DateInput) => {
  return format(toDate(date), 'MMMM dd, yyyy');
};

/** Formats a date as "dd MMM yyyy" (e.g., "15 Jan 2026") for compact display. */
export const formatDateShort = (date: Date | string) => {
  return format(toDate(date), 'dd MMM yyyy');
};

/**
 * Compact date+time for an inline entry/exit label: "9 Oct 9:00 AM" (PB-5381).
 *
 * Deliberately drops the year and the weekday. This renders inside a single product-row line
 * beside a price and a location, where the alternatives either wrap or truncate - and the search
 * that produced the row already pinned the year.
 *
 * ## Invariants:
 * - Converts through `toDate`, never `new Date(string)`. That is this module's documented trap:
 *   ES-262 reads a date-only string as UTC midnight, so a behind-UTC zone renders the previous
 *   day (PB-4476, and again in PB-3276). A caller should not have to know which shapes the api
 *   happens to send today in order to be safe.
 * - Returns `undefined` rather than "Invalid Date" for an absent or unparseable value. The words
 *   on a price row tell the customer nothing and look like their own mistake.
 */
export const formatWindowDateTime = (date: DateInput | undefined): string | undefined => {
  if (date == null || date === '') return undefined;
  const parsed = toDate(date);
  return isValid(parsed) ? format(parsed, 'd MMM h:mm a') : undefined;
};

/** Compact date range for inline labels: "13 – 31 Jul 2026" (same month), "13 Jul – 2 Aug 2026" (same year), else full. */
export const formatDateRangeShort = (start: DateInput, end: DateInput): string => {
  const s = toDate(start);
  const e = toDate(end);
  const sameYear = s.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && s.getMonth() === e.getMonth();
  if (sameMonth) return `${format(s, 'd')} – ${format(e, 'd MMM yyyy')}`;
  if (sameYear) return `${format(s, 'd MMM')} – ${format(e, 'd MMM yyyy')}`;
  return `${format(s, 'd MMM yyyy')} – ${format(e, 'd MMM yyyy')}`;
};

/** Returns true if dateA is before dateB (calendar days only). */
export const isDateBefore = (dateA: DateInput, dateB: DateInput) => {
  return isBefore(toDate(dateA), toDate(dateB));
};

/** Date formats accepted for user-supplied permit / employment dates (legacy parity). */
export const PERMIT_DATE_FORMATS = ['dd/MM/yy', 'MM/dd/yyyy', 'yyyy-MM-dd'];

/**
 * Strictly parses `value` against `formats` in order, returning the first exact
 * match or null. Re-formats each candidate and compares back so rolled-over
 * matches (e.g. "13/45/99") are rejected.
 */
export function parseFlexibleDate(
  value: string | undefined | null,
  formats: string[],
  reference: Date = new Date()
): Date | null {
  if (!value) return null;
  for (const fmt of formats) {
    const parsed = parse(value, fmt, reference);
    if (isValid(parsed) && format(parsed, fmt) === value) {
      return parsed;
    }
  }
  return null;
}

/**
 * Returns a Date representing the start of "today" in the garage timezone.
 * Use this instead of `new Date()` for DatePicker `minDate` props so the
 * boundary is based on the garage's local date, not the browser's local date.
 *
 * @param timezone IANA timezone ID (e.g. "America/New_York"). Falls back to browser local time.
 */
export const garageToday = (timezone?: string): Date => {
  if (!timezone) return startOfDay(new Date());
  const zoned = toZonedTime(new Date(), timezone);
  return startOfDay(zoned);
};

/**
 * Get the current date/time in the garage timezone (for all date comparisons).
 * Returns a Date whose components (year, month, day, hours) reflect garage wall-clock time.
 */
export const garageNow = (timezone?: string): Date => {
  if (!timezone) return new Date();
  return toZonedTime(new Date(), timezone);
};

// ---------------------------------------------------------------------------
// Garage-timezone utilities (matches old app's setZone / setGarageZoneWithStart / castOtherZoneToLocalZone)
// ---------------------------------------------------------------------------

/**
 * **Display**: Format an API date value in the garage timezone for UI.
 * Old app equivalent: `setZone(date, garageAccount).format('MM/DD/YYYY')`
 *
 * Parses via {@link toDate}, so it accepts every shape the API returns — ISO
 * strings, epoch millis (number), and numeric epoch strings. Using `new Date()`
 * directly here dropped payment rows: `pmt.paymentDate` arrives as epoch millis,
 * and `new Date("1782320271000")` is Invalid Date, so the catch printed the raw
 * digits in the Due Date column (PB-3276).
 *
 * @param date     ISO string, epoch millis, numeric string, or Date from the API
 * @param timezone IANA timezone ID (e.g. "America/New_York"). Falls back to UTC.
 * @param fmt      date-fns format token string. Default: 'MM/dd/yyyy'
 */
export const formatDateInGarageTz = (
  date: DateInput | undefined,
  timezone?: string,
  fmt: string = 'MM/dd/yyyy'
): string => {
  if (!date) return '';
  // A bare calendar date has no instant to convert, so converting it can only shift the day —
  // print it as-is. Legacy applies no zone conversion on these surfaces either (PB-4476).
  if (typeof date === 'string' && DATE_ONLY_PATTERN.test(date)) {
    return format(parseCalendarDate(date), fmt);
  }
  try {
    return formatInTimeZone(toDate(date), timezone || 'UTC', fmt);
  } catch {
    return typeof date === 'string' ? date : String(date);
  }
};

/**
 * Like {@link formatDateInGarageTz}, but returns '' for dates that do not APPLY —
 * mirroring the back-office's `visibleDates` rule so both surfaces hide the same
 * fields (PB-3686).
 *
 * ## Invariants:
 * - Open-ended permits carry a SYNTHETIC end date (start + 100 years, set by the
 *   api when no end applies), not a real one. Anything beyond `showUptoMonths`
 *   (default 120, matching the back-office permit view) is treated as "no end
 *   date" and hidden rather than shown as a nonsense year.
 * - Epoch / pre-1971 values mean "not set" (the API's zero default, PB-1478) and
 *   are also hidden.
 */
export const formatApplicableDateInGarageTz = (
  date: DateInput | undefined,
  timezone?: string,
  fmt: string = 'MM/dd/yyyy',
  showUptoMonths: number = 120
): string => {
  if (!date) return '';
  try {
    const parsed = toDate(date);
    if (!isValid(parsed) || parsed.getFullYear() <= 1970) return '';
    if (parsed.getTime() > addMonths(new Date(), showUptoMonths).getTime()) return '';
  } catch {
    return '';
  }
  return formatDateInGarageTz(date, timezone, fmt);
};

/**
 * Resolves a garage IANA timezone id from `garage.timezone`. The API serializes this field as a
 * bare string (e.g. "America/New_York") though the generated type models it as `{ id }`, so accept
 * either shape (read as `unknown`); returns '' when absent. Pair with formatDateInGarageTz so
 * garage-local dates don't silently fall back to UTC and roll forward a day (PB-1736).
 */
export const resolveGarageTimezone = (tz: unknown): string => {
  if (typeof tz === 'string') return tz;
  if (tz && typeof tz === 'object' && 'id' in tz) {
    const id = (tz as { id?: unknown }).id;
    return typeof id === 'string' ? id : '';
  }
  return '';
};

/**
 * **API submit**: Convert a DatePicker Date to start-of-day (or end-of-day)
 * in the garage timezone, returned as an ISO string.
 * Old app equivalent: `setGarageZoneWithStart(date, garage, isEndOfDay)`
 *
 * @param date     Date from the browser DatePicker (local time)
 * @param timezone IANA timezone ID
 * @param endOfDay If true, use 23:59:59.999 instead of 00:00:00
 */
export const toGarageTzISOString = (date: Date, timezone?: string, endOfDay?: boolean): string => {
  // Extract the calendar date the user picked (ignoring browser timezone)
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();

  // Build a Date at start-of-day (or end-of-day) in the garage timezone
  const wallClock = endOfDay ? new Date(y, m, d, 23, 59, 59, 999) : new Date(y, m, d, 0, 0, 0, 0);

  if (!timezone) return wallClock.toISOString();
  return fromZonedTime(wallClock, timezone).toISOString();
};

/**
 * Reinterprets a picked wall-clock instant as a time in the garage timezone and
 * returns the UTC ISO string. Preserves the full time-of-day (hour/minute),
 * unlike {@link toGarageTzISOString} which forces start/end-of-day.
 *
 * Mirrors the legacy reservation site's `setOtherZone`: the operator picks an
 * exit time meaning the garage's local clock, so the same wall-clock must be
 * sent as the garage-timezone instant — otherwise the rate engine computes for
 * the wrong absolute time when the browser timezone differs from the garage.
 *
 * @param date Picked date/time (browser-local wall-clock).
 * @param timezone IANA garage timezone id; falls back to browser local when absent.
 */
export const toGarageZonedISOString = (date: Date, timezone?: string): string =>
  timezone ? fromZonedTime(date, timezone).toISOString() : date.toISOString();

/**
 * **Picker feed (boundary):** Convert an absolute instant into a "wall-clock" Date
 * whose browser-local fields (getHours/getDate/…) equal the instant's *garage-local*
 * components. Use this to feed an absolute instant into a browser-local date/time
 * picker so the picker shows garage-local numbers. Returns the instant unchanged
 * when no timezone is given. Pair with {@link fromGarageWallClock} to read the
 * picker's output back. (PB-3411)
 *
 * The result is NOT a real instant — it is only meaningful as input to a
 * browser-local picker; convert it back with `fromGarageWallClock` before use.
 */
export const toGarageWallClock = (date: DateInput, timezone?: string): Date =>
  timezone ? toZonedTime(toDate(date), timezone) : toDate(date);

/**
 * **Picker read-back (boundary):** Inverse of {@link toGarageWallClock}. Interpret
 * a browser-local wall-clock Date emitted by a picker as a *garage-local* time and
 * return the correct absolute UTC instant. Returns the Date unchanged when no
 * timezone is given. Mirrors {@link toGarageZonedISOString} but returns a Date. (PB-3411)
 */
export const fromGarageWallClock = (date: Date, timezone?: string): Date =>
  timezone ? fromZonedTime(date, timezone) : date;

/**
 * Resolves an IANA timezone id from a garage `timezone` value.
 *
 * The generated `DateTimeZone` type models this as an object (`{ id, fixed }`),
 * but several endpoints (notably `/user/domain/garageAccounts`) serialize it as a
 * plain IANA string (e.g. `"America/New_York"`). Accept both so callers don't
 * silently read `undefined` off a string (which drops the timezone entirely).
 */
export const resolveTimezoneId = (timezone: unknown): string | undefined => {
  if (typeof timezone === 'string') return timezone || undefined;
  if (timezone && typeof timezone === 'object') {
    const id = (timezone as { id?: unknown }).id;
    return typeof id === 'string' ? id || undefined : undefined;
  }
  return undefined;
};

/**
 * **DatePicker load**: Convert an API ISO date (stored in garage timezone)
 * to a local Date object that shows the same calendar date in the DatePicker.
 * Old app equivalent: `castOtherZoneToLocalZone(date, timezone)`
 *
 * @param isoDate  ISO date string from the API
 * @param timezone IANA timezone ID
 */
export const fromGarageTzToLocal = (
  isoDate: string | undefined,
  timezone?: string
): Date | null => {
  if (!isoDate) return null;
  if (!timezone) return new Date(isoDate);
  // Convert to the garage timezone's wall-clock time, then use that as a local date
  const zoned = toZonedTime(new Date(isoDate), timezone);
  return new Date(zoned.getFullYear(), zoned.getMonth(), zoned.getDate());
};

/**
 * Like `fromGarageTzToLocal`, but treats an uninitialized epoch-0 / pre-1971 timestamp
 * (which the API returns for "no date set") as absent and returns null. Without this guard
 * a `0` / "1970-01-01" value parses to a valid Date, the `?? fallback` never fires, and the
 * field renders as "12/1969". PB-1478
 */
export const fromGarageTzToLocalIfSet = (
  isoDate: string | undefined,
  timezone?: string
): Date | null => {
  const parsed = fromGarageTzToLocal(isoDate, timezone);
  if (!parsed || isNaN(parsed.getTime()) || parsed.getFullYear() <= 1970) return null;
  return parsed;
};

/**
 * @deprecated Use `toGarageTzISOString` instead.
 *
 * Converts a local Date to an ISO string as if the wall-clock time were in the
 * given IANA timezone. This replicates the old app's `setOtherZone()` behaviour:
 * the same hours/minutes are preserved but the UTC offset is changed to match
 * the target timezone.
 *
 * Example: local 10:00 AM EST → returns ISO string for 10:00 AM in
 * "America/Los_Angeles" (PST offset).
 */
export const toGarageTimezone = (date: Date, timezone: string): string => {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const h = date.getHours();
  const min = date.getMinutes();
  const s = date.getSeconds();
  const ms = date.getMilliseconds();

  // Format the date in the target timezone to find its UTC offset
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? '+00:00';
  // tzPart is e.g. "GMT-08:00" or "GMT+05:30" — extract the offset portion
  const offset = tzPart.replace('GMT', '') || '+00:00';

  // Build ISO string: same wall-clock time with the target timezone's offset
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  const iso = `${y}-${pad(m + 1)}-${pad(d)}T${pad(h)}:${pad(min)}:${pad(s)}.${pad(ms, 3)}${offset}`;

  return new Date(iso).toISOString();
};

/**
 * Converts a zone rate cycle increment to minutes.
 *
 * @param increment - the increment value (e.g., 60 for MINUTES, 2 for DAYS)
 * @param rateCycle - the rate cycle unit (MINUTES, DAYS, CALENDAR_DAYS, MONTHS, CALENDAR_MONTHS)
 * @returns the equivalent duration in minutes
 */
/**
 * Computes the exit Date for a cycle-based parking stay.
 *
 * - MINUTES / DAYS: simple offset from now. MONTHS: same date N months later (see invariants).
 * - CALENDAR_DAYS: ends at the end of the Nth calendar day (today is day 1), or at the daily
 *   cut-off on that day. Cut-offs 12:00–23:59 apply the same day; 00:00–11:59 the next day.
 * - CALENDAR_MONTHS: ends at the end of the last day of the Nth calendar month (this month is
 *   month 1), with the same cut-off logic on that day.
 *
 * ## Invariants:
 * - CALENDAR_* exits follow the api that prices them (`ZoneRateServiceImpl.calculateExitTime`,
 *   PB-1987 / PB-1988): end of the (N-1)-th day / month after entry, in the garage timezone.
 *   They match in normal cases. The api steps the N-1 days / months on the UTC calendar before
 *   taking the garage end of day / month, so it can differ in narrow windows: the first hours
 *   after garage midnight, garages east of UTC, and days next to a DST change.
 *   Why (PB-5042): VEC-1806's `- 1` was applied to months without the jump to month end, so a
 *   full month's price booked a one-day stay (Sep 15 → Sep 16).
 * - "End of day" is 23:59:59.999 of that day, not 00:00 of the next. Why: the api, garage hours
 *   and the rest of the app use 23:59:59.999, and 00:00 next day is shown to the parker as the
 *   NEXT date ("12:00 AM, 16 Sep" for a stay that ends on 15 Sep).
 * - MONTHS is a rolling calendar month (same date next month, clamped to the month's last day),
 *   NOT 30 days. Why (PB-5466): the api prices `start.plusMonths(n)` — 61 days for 2 months from
 *   Sep 30 — so 30-day months booked a shorter stay than was paid for. It is added on the UTC
 *   calendar on purpose, because the api's `start` is `DateTime.now()` in the JVM zone (UTC,
 *   `ZoneRateServiceImpl.java:188`): any other zone moves the exit an hour off the priced span
 *   when the month crosses a DST change, and the touchless page re-prices entry → exit. If the
 *   api moves `start` to the garage timezone, move this to the garage timezone too.
 *
 * @param increment  cycle increment value (e.g. 2 for "2 days")
 * @param rateCycle  MINUTES | DAYS | CALENDAR_DAYS | MONTHS | CALENDAR_MONTHS
 * @param dailyCutOff  "HH:mm" cut-off string (only used for CALENDAR_* cycles)
 * @param enableDailyCutOff  whether the cut-off is active
 * @param now  reference time (defaults to current time)
 * @param timezone  IANA garage timezone id. For CALENDAR_* cycles the calendar
 *   day/month boundary and daily cut-off resolve in THIS timezone (the garage's
 *   local day), not the viewer's browser day. Omit to use browser-local (PB-3411).
 */
export function computeExitDate(
  increment: number,
  rateCycle?: string,
  dailyCutOff?: string,
  enableDailyCutOff?: boolean,
  now?: Date,
  timezone?: string
): Date {
  const base = now ?? new Date();

  switch (rateCycle) {
    case 'MINUTES':
      return new Date(base.getTime() + increment * 60_000);

    case 'DAYS':
      return new Date(base.getTime() + increment * 24 * 60 * 60_000);

    case 'CALENDAR_DAYS': {
      return calendarOffset(base, increment, 'days', dailyCutOff, enableDailyCutOff, timezone);
    }

    case 'MONTHS':
      return addUtcMonthsClamped(base, increment);

    case 'CALENDAR_MONTHS': {
      return calendarOffset(base, increment, 'months', dailyCutOff, enableDailyCutOff, timezone);
    }

    default:
      return new Date(base.getTime() + increment * 60_000);
  }
}

/**
 * Adds `months` calendar months on the UTC calendar, keeping the UTC time of day. When the target
 * month is shorter than the start day, clamps to its last day (Jan 31 + 1 → Feb 28/29), as Joda's
 * `plusMonths` does. See the MONTHS invariant on {@link computeExitDate}.
 */
function addUtcMonthsClamped(base: Date, months: number): Date {
  const result = new Date(base.getTime());
  const day = result.getUTCDate();
  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)).getUTCDate();
  result.setUTCDate(Math.min(day, lastDay));
  return result;
}

/**
 * Resolves a calendar-based end date at the daily cut-off.
 *
 * Cut-off 12:00–23:59 → applies to the current calendar day boundary.
 * Cut-off 00:00–11:59 → applies to the next calendar day boundary.
 * No cut-off → end of that day (23:59:59.999).
 */
function calendarOffset(
  base: Date,
  increment: number,
  unit: 'days' | 'months',
  dailyCutOff?: string,
  enableDailyCutOff?: boolean,
  timezone?: string
): Date {
  // When a garage timezone is given, do the calendar-boundary + cut-off math on
  // the garage's LOCAL wall-clock: toZonedTime yields a Date whose local fields
  // equal the garage-local components, we mutate those fields, then fromZonedTime
  // converts back to the correct absolute UTC instant. Without a tz, operate in
  // browser-local (unchanged legacy behavior). (PB-3411)
  const target = timezone ? toZonedTime(base, timezone) : new Date(base);

  // The current partial day / month counts as the first unit, so move N - 1 (VEC-1806).
  if (unit === 'days') {
    target.setDate(target.getDate() + increment - 1);
  } else {
    // Go to the 1st first so a month-end entry cannot overflow (Jan 31 + 1 month ≠ Mar 3),
    // then to the last day of the (N-1)-th month after entry (PB-5042).
    target.setDate(1);
    target.setMonth(target.getMonth() + increment - 1);
    target.setMonth(target.getMonth() + 1, 0);
  }

  if (enableDailyCutOff && dailyCutOff) {
    const [hStr, mStr] = dailyCutOff.split(':');
    const cutH = parseInt(hStr, 10) || 0;
    const cutM = parseInt(mStr, 10) || 0;

    // Set target to the cut-off time on that day
    target.setHours(cutH, cutM, 0, 0);

    // Cut-offs before noon wrap to the next day
    // (e.g. 02:00 cut-off means "2 AM tomorrow" relative to the calendar day)
    if (cutH < 12) {
      target.setDate(target.getDate() + 1);
    }
  } else {
    // No cut-off: end of the last calendar day (see the end-of-day invariant on computeExitDate)
    target.setHours(23, 59, 59, 999);
  }

  return timezone ? fromZonedTime(target, timezone) : target;
}

/** @deprecated Use computeExitDate instead — kept for backward compatibility */
export function convertCycleIncrementToMinutes(increment: number, rateCycle?: string): number {
  const exit = computeExitDate(increment, rateCycle);
  return Math.round((exit.getTime() - Date.now()) / 60_000);
}

/**
 * Formats a duration in minutes to a compact human-readable string
 * using months (M), days (d), hours (h), and minutes (m).
 * Only non-zero segments are shown.
 *
 * @example
 * formatDurationShort(135)       // "2h 15m"
 * formatDurationShort(45)        // "45m"
 * formatDurationShort(1500)      // "1d 1h 0m"
 * formatDurationShort(44640)     // "1M 0d 0h 0m"
 * formatDurationShort(0)         // ""
 */
export function formatDurationShort(totalMinutes: number): string {
  if (totalMinutes < 1) return '';

  const MINUTES_PER_HOUR = 60;
  const MINUTES_PER_DAY = 60 * 24;
  const MINUTES_PER_MONTH = MINUTES_PER_DAY * 31; // approximate

  const months = Math.floor(totalMinutes / MINUTES_PER_MONTH);
  let remaining = totalMinutes % MINUTES_PER_MONTH;
  const days = Math.floor(remaining / MINUTES_PER_DAY);
  remaining = remaining % MINUTES_PER_DAY;
  const hours = Math.floor(remaining / MINUTES_PER_HOUR);
  const mins = remaining % MINUTES_PER_HOUR;

  const parts: string[] = [];
  if (months > 0) parts.push(`${months}M`);
  if (days > 0 || months > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0 || months > 0) parts.push(`${hours}h`);
  parts.push(`${mins}m`);

  return parts.join(' ');
}

/**
 * Formats an elapsed duration (in seconds) as a clock-style `H:mm:ss` string —
 * hours un-padded, minutes and seconds zero-padded. Negative inputs clamp to 0.
 *
 * @example
 * formatElapsedClock(4329)  // "1:12:09"
 * formatElapsedClock(59)    // "0:00:59"
 * formatElapsedClock(-5)    // "0:00:00"
 */
export function formatElapsedClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** A resolved calendar window — both bounds inclusive. */
export interface DateWindow {
  start: Date;
  end: Date;
}

/**
 * Resolves the billing window of a CUSTOM-cycle permit rate from its `MM/DD` cycle
 * bounds, anchored on the customer's permit start date. Returns `null` when either
 * bound is missing or unparseable, so callers can omit the period rather than render
 * an "Invalid date" range.
 *
 * ## Invariants:
 * - The bounds are parsed in the permit start date's year, not the current year.
 *   Why: the legacy app parsed against today, which resolved a permit starting next
 *   January into this January's window. The wrap-around adjustments below make the
 *   two agree for any permit starting in the current year.
 * - A window whose end month/day precedes its start month/day spans the year boundary
 *   (e.g. an academic 09/01–05/31 permit), so `end` may land in the following year.
 */
export function resolveCustomCycleWindow(
  permitStart: DateInput,
  cycleStartDayMonth?: string | null,
  cycleEndDayMonth?: string | null
): DateWindow | null {
  if (!cycleStartDayMonth || !cycleEndDayMonth) return null;

  const anchor = startOfDay(toDate(permitStart));
  if (!isValid(anchor)) return null;

  let end = parse(cycleEndDayMonth, 'MM/dd', anchor);
  let start = parse(cycleStartDayMonth, 'MM/dd', anchor);
  if (!isValid(end) || !isValid(start)) return null;

  if (isBefore(end, anchor)) end = addYears(end, 1);
  if (isBefore(start, anchor)) start = addYears(start, 1);
  if (isAfter(start, end)) start = subYears(start, 1);

  return { start, end };
}

/**
 * Resolves the parking period a CUSTOM-cycle permit actually covers — the rate's
 * billing window, clamped forward to the customer's start date when they join
 * mid-window. Returns `null` when the window can't be resolved.
 *
 * ## Invariants:
 * - `useRateBillingCycleStartDate` rates always report the full window.
 *   Why: those permits are billed from the cycle's own start regardless of when the
 *   customer signs up, so reporting their start date would understate what they pay for.
 */
export function resolveCustomPermitPeriod(
  permitStart: DateInput,
  cycleStartDayMonth?: string | null,
  cycleEndDayMonth?: string | null,
  useRateBillingCycleStartDate?: boolean
): DateWindow | null {
  const window = resolveCustomCycleWindow(permitStart, cycleStartDayMonth, cycleEndDayMonth);
  if (!window || useRateBillingCycleStartDate) return window;

  const anchor = startOfDay(toDate(permitStart));
  const joinsMidWindow = isAfter(anchor, window.start) && isBefore(anchor, window.end);
  return joinsMidWindow ? { start: anchor, end: window.end } : window;
}

/** Formats a resolved window as "MM/DD/YYYY - MM/DD/YYYY" (or a caller-supplied separator). */
export const formatDateWindow = (window: DateWindow, separator = ' - '): string =>
  `${formatDate(window.start)}${separator}${formatDate(window.end)}`;

/**
 * The calendar year to print in a copyright line.
 *
 * ## Invariants:
 * - With `timeZone` (an IANA id, the company's) the year is the year it is in THAT zone right now,
 *   so a footer read just after midnight UTC on New Year's Day still shows the company's year.
 * - Without `timeZone` it is the viewer's local year (`now.getFullYear()`). `Theme` carries no
 *   timezone yet, so today's callers pass nothing; the year can differ from the company's only on
 *   New Year's Eve, in zones that differ from the viewer's.
 * - An id the runtime rejects falls back to the local year and logs; it never throws into render.
 *
 * @param now the instant to read
 * @param timeZone optional IANA zone id
 */
export function copyrightYear(now: Date, timeZone?: string): number {
  if (!timeZone) return now.getFullYear();
  try {
    return Number(formatInTimeZone(now, timeZone, 'yyyy'));
  } catch (error) {
    logger.warn('date-time-utils: unusable time zone for copyright year', {
      feature: 'date-time-utils',
      route: 'copyrightYear',
      timeZone,
      error: error instanceof Error ? error.message : 'unknown',
    });
    return now.getFullYear();
  }
}
